import { Share2 } from "lucide-react";
import { motion } from "framer-motion";
import TrailingStatus from "@/components/TrailingStatus";
import { actionTone, fmt, scoreColor, toneClasses } from "@/lib/format";

const LEVEL_TONE = { "Stop Loss": "text-[#FF3366]", Target: "text-[#00FF66]", Trailing: "text-[#00B8FF]" };
const val = (v) => (typeof v === "number" ? (Number.isInteger(v) ? v.toLocaleString("en-IN") : fmt(v)) : v);

export default function SignalCard({ item, selected, onSelect, onShare, showFutures }) {
  const tone = actionTone(item.action);
  const up = item.chg_pct >= 0;
  const glow = item.strong ? (tone === "bear" ? "rx-glow-red" : "rx-glow-green") : "";
  return (
    <motion.div layout initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      onClick={() => onSelect?.(item.symbol)} data-testid={`signal-card-${item.symbol}`}
      className={`rx-card rx-lift p-5 cursor-pointer ${glow} ${selected ? "!border-[#007AFF]/70 shadow-[0_0_30px_rgba(0,122,255,.18)]" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-base font-bold tracking-tight truncate">{item.symbol}</div>
          <div className="text-xs text-gray-500 truncate">{item.name}</div>
        </div>
        <div className="text-right">
          <div className="font-data text-base font-bold">{fmt(item.ltp)}</div>
          <div className={`font-data text-xs ${up ? "text-[#00FF66]" : "text-[#FF3366]"}`}>{up ? "+" : ""}{fmt(item.chg_pct)}%</div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <span className={`font-data text-xs font-bold px-2.5 py-1 rounded-md border ${toneClasses[tone]}`} data-testid={`signal-action-${item.symbol}`}>{item.action}</span>
          <div className="font-display text-xl font-bold mt-2.5 truncate" data-testid={`signal-strike-${item.symbol}`}>{item.strike || item.instrument}</div>
          {item.strike && <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">Suggested strike</div>}
        </div>
        <div className="shrink-0 h-16 w-16 rounded-full grid place-items-center border-2" style={{ borderColor: scoreColor(item.score), boxShadow: item.strong ? `0 0 20px ${scoreColor(item.score)}55` : "none" }}>
          <span className="font-data text-xl font-bold" style={{ color: scoreColor(item.score) }} data-testid={`signal-score-${item.symbol}`}>{item.score.toFixed(0)}</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-px bg-white/5 rounded-xl overflow-hidden">
        {item.levels.map((l) => (
          <div key={l.k} className="bg-[#0B0E13] p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{l.k}</div>
            <div className={`font-data text-base font-semibold mt-0.5 ${LEVEL_TONE[l.k] || ""}`}>{val(l.v)}</div>
          </div>
        ))}
      </div>

      {showFutures && (
        <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] font-data text-gray-400">
          <div>FUT <span className="text-white">{fmt(item.fut)}</span></div>
          <div>PREM <span className={item.premium_pts >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"}>{fmt(item.premium_pts)}</span></div>
          <div>OI <span className="text-white">{fmt(item.oi_chg_pct)}%</span></div>
          <div className="col-span-2 text-gray-300">{item.buildup}</div>
          <div>VOL <span className="text-white">{item.volume_x}x</span></div>
        </div>
      )}

      {"trail" in item && <div className="mt-3"><TrailingStatus trail={item.trail} testId={`trail-status-${item.symbol}`} /></div>}

      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="text-[11px] text-gray-500 line-clamp-1">{item.note}</p>
        {onShare && (
          <button onClick={(e) => { e.stopPropagation(); onShare(item); }} data-testid={`share-signal-${item.symbol}`}
            className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-[#00FF66] hover:bg-white/5 transition-colors" aria-label="Share signal">
            <Share2 size={15} />
          </button>
        )}
      </div>
    </motion.div>
  );
}

export function FeaturedSignal({ item, liveScore, liveLtp, onShare, segment }) {
  if (!item) return <div className="rx-hero-card h-full min-h-[420px] animate-pulse" />;
  const tone = actionTone(item.action);
  const toneHex = tone === "bull" ? "#00FF66" : tone === "bear" ? "#FF3366" : "#FFCC00";
  const score = liveScore ?? item.score;
  const ltp = liveLtp ?? item.ltp;
  const isOpt = segment === "option-buyer" || segment === "option-seller";
  return (
    <motion.div key={item.symbol} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }}
      className="rx-hero-card p-6 lg:p-7 h-full flex flex-col" style={{ "--tone": toneHex, boxShadow: `0 0 60px ${toneHex}1F` }} data-testid="featured-signal">
      <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl pointer-events-none" style={{ background: `${toneHex}22` }} />
      <div className="relative flex items-start justify-between gap-3">
        <div>
          <div className="rx-label flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: toneHex }} /> Featured signal</div>
          <div className="font-display text-2xl font-bold mt-2">{item.symbol}</div>
          <div className="text-xs text-gray-500">{item.name}</div>
        </div>
        <div className="text-right">
          <div className="font-data text-2xl font-bold" data-testid="featured-ltp">{fmt(ltp)}</div>
          <div className={`font-data text-sm ${item.chg_pct >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"}`}>{item.chg_pct >= 0 ? "+" : ""}{fmt(item.chg_pct)}%</div>
        </div>
      </div>

      <div className="relative mt-6 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <span className={`font-data text-sm font-bold px-3 py-1.5 rounded-lg border ${toneClasses[tone]}`} data-testid="featured-action">{item.action}</span>
          <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-gray-500">{isOpt ? "Suggested strike" : "Instrument"}</div>
          <div className="font-display text-3xl lg:text-4xl font-black tracking-tight mt-1 break-words" style={{ color: tone === "neutral" ? "#fff" : toneHex, textShadow: tone === "neutral" ? "none" : `0 0 24px ${toneHex}55` }} data-testid="featured-strike">
            {item.strike || item.instrument}
          </div>
        </div>
        <div className="shrink-0 text-center">
          <div className="relative h-24 w-24 lg:h-28 lg:w-28 rounded-full grid place-items-center" style={{ background: `conic-gradient(${toneHex} ${score * 3.6}deg, rgba(255,255,255,.06) 0)` }}>
            <div className="absolute inset-[6px] rounded-full bg-[#0B0E13] grid place-items-center">
              <span className="font-data text-3xl font-bold" style={{ color: scoreColor(score) }} data-testid="featured-score">{score.toFixed(0)}</span>
            </div>
            {score >= 75 && <span className="absolute inset-0 rounded-full rx-pulse-ring" style={{ boxShadow: `0 0 0 2px ${toneHex}` }} />}
          </div>
          <div className="rx-label mt-2">{segment === "investors" ? "Investor score" : "Strength"}</div>
        </div>
      </div>

      <div className="relative mt-6 grid grid-cols-2 gap-3">
        {item.levels.map((l) => (
          <div key={l.k} className="rounded-xl border border-white/5 bg-black/30 p-3.5">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{l.k}</div>
            <div className={`font-data text-xl lg:text-2xl font-bold mt-1 ${LEVEL_TONE[l.k] || ""}`} data-testid={`featured-level-${l.k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{val(l.v)}</div>
          </div>
        ))}
      </div>

      {"trail" in item && <div className="relative mt-4"><TrailingStatus trail={item.trail} large testId="featured-trail-status" /></div>}

      <div className="relative mt-auto pt-4 flex items-center justify-between gap-3">
        <p className="text-xs text-gray-400">{item.note}</p>
        <button onClick={() => onShare(item)} data-testid="featured-share-btn" className="shrink-0 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold hover:border-[#00FF66]/50 hover:text-[#00FF66] transition-colors">
          <Share2 size={14} /> Share
        </button>
      </div>
    </motion.div>
  );
}
